import React, { ReactNode } from "react";
import { Scoped } from "kremling";

import { CpSelect } from "../cp-select/cp-select.component";
import {
  defaultSearchFilter,
  filterAlpha,
  SelectDataItem,
} from "../cp-select/common/utils";
import styles from "./../cp-select/cp-select.styles.scss";

export type CpSelectMultiPillsProps<TData, TValue> = {
  allowTextWrap?: boolean;
  appendTo?: HTMLElement | "parent";
  autoFocus?: boolean;
  className?: string;
  data?: TData[];
  disabled?: boolean;
  isGroupData?: boolean;
  groupType?: "header" | "border";
  loading?: boolean;
  onBlur?: () => void;
  onChange: (value: NoInfer<TValue>[]) => void;
  onClose?: () => void;
  onOpen?: () => void;
  teamMember?: boolean;
  placeholder?: string;
  preventKeyOpen?: boolean;
  preventOutsideClickUntilClosed?: boolean;
  renderFooter?: (params: any) => ReactNode;
  renderPill?: (value: TValue) => ReactNode;
  searchFilter?: boolean | ((item: any) => boolean);
  searchOnChange?: (value: string) => void;
  searchValue?: string;
  clearOnSelect?: boolean;
  stopPropagation?: boolean;
  transformData?: (item: TValue) => SelectDataItem;
  triggerRef?: React.RefObject<any>;
  value: TValue[];
  contentWidth?: number | "sm" | "md" | "lg" | "block" | "auto";
  error?: boolean | string;
  renderEmptyState?: (params: any) => ReactNode;
  emptyStateAction?: (params: {
    onChange: (value: TValue[]) => void;
    close: () => void;
  }) => void;
};

function CpSelectMultiPillsBase<TData, TValue>(
  props: CpSelectMultiPillsProps<TData, TValue>,
) {
  const {
    allowTextWrap,
    appendTo,
    autoFocus,
    className,
    data,
    disabled,
    isGroupData,
    groupType,
    loading,
    onBlur,
    onChange,
    onClose,
    onOpen,
    teamMember,
    placeholder,
    preventKeyOpen,
    preventOutsideClickUntilClosed,
    renderFooter,
    renderPill,
    searchFilter,
    searchOnChange,
    searchValue,
    clearOnSelect,
    stopPropagation,
    transformData,
    triggerRef,
    value,
    contentWidth = "block",
    error,
    renderEmptyState,
    emptyStateAction,
  } = props;
  return (
    <Scoped css={styles}>
      <CpSelect<TData, TValue>
        allowTextWrap={allowTextWrap}
        appendTo={appendTo}
        className={className}
        contentWidth={contentWidth}
        data={data}
        disabled={disabled}
        insertDone
        isGroupData={isGroupData}
        groupType={groupType}
        loading={loading}
        onChange={onChange as (value: TValue | TValue[]) => void}
        onClose={onClose}
        onOpen={onOpen}
        preventKeyOpen={preventKeyOpen}
        preventOutsideClickUntilClosed={preventOutsideClickUntilClosed}
        renderFooter={renderFooter || (CpSelect as any).Footer}
        renderGroup={(CpSelect as any).Group}
        renderItem={(CpSelect as any).ItemMulti}
        renderEmptyState={renderEmptyState}
        emptyStateAction={emptyStateAction}
        renderTrigger={(triggerProps) => {
          const TriggerComponent = (CpSelect as any).TriggerPills;
          return (
            <TriggerComponent
              autoFocus={autoFocus}
              onBlur={onBlur}
              teamMember={teamMember}
              renderPill={renderPill || ((val: any) => val.name)}
              placeholder={placeholder}
              error={error}
              {...triggerProps}
            />
          );
        }}
        searchFilter={defaultSearchFilter(searchFilter, searchValue)}
        searchOnChange={searchOnChange}
        searchValue={searchValue}
        clearOnSelect={clearOnSelect}
        stopPropagation={stopPropagation}
        transformData={transformData}
        triggerRef={triggerRef}
        triggerIsBlock
        value={value}
      />
      {typeof error === "string" && (
        <div className="cp-select--error-text">{error}</div>
      )}
    </Scoped>
  );
}

// Export type that includes static method
export type CpSelectMultiPillsComponent = (<TData, TValue>(
  props: CpSelectMultiPillsProps<TData, TValue>,
) => React.JSX.Element) & {
  filterAlpha: typeof filterAlpha;
};

// Create typed component with static properties
const CpSelectMultiPills =
  CpSelectMultiPillsBase as CpSelectMultiPillsComponent;

// Attach static method
CpSelectMultiPills.filterAlpha = filterAlpha;

// Export the properly typed component
export { CpSelectMultiPills };
